import { type FC } from 'react';

import { Divider } from '../Divider';
import { Text } from '../Typography';
import {
  actionStyles,
  badgeLabelStyles,
  badgeStyles,
  ctaStyles,
  eyebrowStyles,
  flushDividerStyles,
  footnoteStyles,
  headerStyles,
  itemListStyles,
  notesStyles,
  rootStyles,
  sectionStyles,
  variantStyles,
} from './PricingDetails.styles';
import {
  type PricingDetailsActionProps,
  type PricingDetailsBadgeProps,
  type PricingDetailsProps,
  type PricingDetailsSectionProps,
} from './PricingDetails.types';
import PricingLine from './PricingLine';
import PricingTotal from './PricingTotal';

const PricingDetailsBadge: FC<PricingDetailsBadgeProps> = ({
  children,
  className,
}) => {
  return (
    <span css={badgeStyles} className={className}>
      <Text
        as="span"
        size="xs"
        uppercase
        fontWeight="semibold"
        color="var(--text-primary)"
        css={badgeLabelStyles}
      >
        {children}
      </Text>
    </span>
  );
};

PricingDetailsBadge.displayName = 'PricingDetails.Badge';

const PricingDetailsAction: FC<PricingDetailsActionProps> = ({
  children,
  href,
  onClick,
  className,
}) => {
  const label = (
    <Text as="span" size="sm" fontWeight="semibold">
      {children}
    </Text>
  );

  if (href) {
    return (
      <a href={href} onClick={onClick} css={actionStyles} className={className}>
        {label}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      css={actionStyles}
      className={className}
    >
      {label}
    </button>
  );
};

PricingDetailsAction.displayName = 'PricingDetails.Action';

const PricingDetailsSection: FC<PricingDetailsSectionProps> = ({
  eyebrow,
  action,
  items,
  total,
  children,
  cta,
  link,
  footnote,
  className,
}) => {
  const hasItems = Boolean(items && items.length > 0);
  const hasSupplement = Boolean(children);
  const hasNotes = Boolean(link || footnote);

  return (
    <section css={sectionStyles} className={className}>
      <div css={headerStyles}>
        <Text
          as="h3"
          size="xs"
          uppercase
          fontWeight="semibold"
          color="var(--text-secondary)"
          css={eyebrowStyles}
        >
          {eyebrow}
        </Text>
        {action}
      </div>

      {hasItems ? (
        <ul css={itemListStyles}>
          {items?.map((item, index) => (
            <li key={`${item.label}-${index}`}>
              <PricingLine {...item} />
            </li>
          ))}
        </ul>
      ) : null}

      {hasItems && (hasSupplement || total) ? (
        <Divider css={flushDividerStyles} />
      ) : null}

      {children}

      {hasSupplement && total ? <Divider css={flushDividerStyles} /> : null}

      {total ? <PricingTotal {...total} /> : null}

      {cta ? <div css={ctaStyles}>{cta}</div> : null}

      {hasNotes ? (
        <div css={notesStyles}>
          {link ? (
            <PricingDetailsAction href={link.href} onClick={link.onClick}>
              {link.label}
            </PricingDetailsAction>
          ) : null}
          {footnote ? (
            <Text size="xs" color="var(--text-secondary)" css={footnoteStyles}>
              {footnote}
            </Text>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

PricingDetailsSection.displayName = 'PricingDetails.Section';

const PricingDetailsRoot: FC<PricingDetailsProps> = ({
  variant = 'default',
  className,
  children,
  ...props
}) => {
  return (
    <div
      css={[rootStyles, variantStyles[variant]]}
      className={className}
      {...props}
    >
      {children}
    </div>
  );
};

PricingDetailsRoot.displayName = 'PricingDetails';

type PricingDetailsComponent = FC<PricingDetailsProps> & {
  Section: typeof PricingDetailsSection;
  Badge: typeof PricingDetailsBadge;
  Action: typeof PricingDetailsAction;
  Line: typeof PricingLine;
  Total: typeof PricingTotal;
};

const PricingDetails = PricingDetailsRoot as PricingDetailsComponent;

PricingDetails.Section = PricingDetailsSection;
PricingDetails.Badge = PricingDetailsBadge;
PricingDetails.Action = PricingDetailsAction;
PricingDetails.Line = PricingLine;
PricingDetails.Total = PricingTotal;

export default PricingDetails;
